import { ChangeDetectionStrategy, Component, Input, inject, computed, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink, RouterLinkActive, Router, NavigationEnd } from '@angular/router';

import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatMenuModule } from '@angular/material/menu';

import { toSignal } from '@angular/core/rxjs-interop';
import { RoutesUrl } from '@app/routes.enum';
import { environment } from '@env/environment';
import { filter, switchMap, of, catchError, map } from 'rxjs';
import { Liga } from '@app/models/liga.model';

import { ServicioBarraSuperior } from '@app/services/general/barra-superior.service';
import { ServicioSesion } from '@app/services/sesion/sesion.service';
import { ThemingService } from '@app/services/general/theming.service';
import { CrmService } from '@app/services/crm/crm.service';
import { EquiposService } from '@app/services/equipos/equipos.service';

interface OpcionMenu {
  nombre: string;
  ruta?: string;
  icono: string;
  hijos?: OpcionMenu[];
  nietos?: OpcionMenu[];
  desplegado?: boolean;
  roles?: string[];
  rolesExcluidos?: string[]; // Se oculta si el rol activo es alguno de estos
  queryParams?: Record<string, string | number>;
  permiso?: string | string[]; // Array = basta con tener cualquiera de esos permisos
  tipo?: ('GLOBAL' | 'LIGA' | 'EQUIPO')[]; // Si se omite, visible para todos
  preserveQueryParams?: boolean;
  requiereConfigCrm?: boolean; // Oculta la opción si el contexto activo no tiene CrmConfig
  requiereLigaEquipo?: boolean; // Oculta la opción si el equipo activo no pertenece a ninguna liga
}

@Component({
  selector: 'app-barra-lateral',
  standalone: true,
  imports: [CommonModule, RouterLink, RouterLinkActive, MatIconModule, MatTooltipModule, MatMenuModule],
  templateUrl: './barra-lateral.component.html',
  styleUrls: ['./barra-lateral.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BarraLateralComponent {
  @Input() liga: Liga | null = null;
  public routesUrl = RoutesUrl;
  public readonly urlAyuda = 'https://torneototal.tawk.help/';
  // Solo donde el entorno carga el chat de tawk.to
  public readonly ayudaDisponible = !!environment.tawkToUrl;

  private _servicioBarra = inject(ServicioBarraSuperior);
  private _router = inject(Router);
  private _sesion = inject(ServicioSesion);
  private _theming = inject(ThemingService);
  private _crmService = inject(CrmService);
  private _equiposService = inject(EquiposService);

  public menuAbierto = this._servicioBarra.menuLateralAbierto;
  public rolActivoSignal = toSignal(this._sesion.rolActivo$);
  private usuarioSignal = toSignal(this._sesion.usuarioActual$);

  private _crmConfigDisponible = toSignal(
    this._sesion.rolActivo$.pipe(
      switchMap(rol => {
        if (!rol) return of(false);
        const filtro = rol.tipo === 'LIGA' ? { liga_id: rol.ligaId } : rol.tipo === 'EQUIPO' ? { equipo_id: rol.equipoId } : {};
        return this._crmService.tieneConfig(filtro).pipe(catchError(() => of(false)));
      }),
    ),
    { initialValue: false },
  );

  // Si el equipo activo pertenece a alguna liga vigente (equipo_ligas).
  private _equipoTieneLiga = toSignal(
    this._sesion.rolActivo$.pipe(
      switchMap(rol => {
        if (rol?.tipo !== 'EQUIPO' || !rol.equipoId) return of(false);
        return this._equiposService.getLigasDeEquipo(rol.equipoId).pipe(
          map(res => (res.ligas ?? []).length > 0),
          catchError(() => of(false)),
        );
      }),
    ),
    { initialValue: false },
  );

  public menuColapsado = signal<boolean>(false);
  public modoOscuro = signal<boolean>(this._theming.esModoOscuroActivo());


  public menuCompleto: OpcionMenu[] = [
    {
      nombre: 'Dashboard',
      ruta: RoutesUrl.DASHBOARD,
      icono: 'tt_am-dashboard',
      preserveQueryParams: true,
      rolesExcluidos: ['ROOT', 'CRM ROOT'],
    },
    {
      // ROOT: primero elige la liga y luego ve su dashboard
      nombre: 'Dashboard liga',
      ruta: RoutesUrl.DASHBOARD,
      icono: 'tt_am-dashboard',
      tipo: ['GLOBAL'],
      roles: ['ROOT', 'CRM ROOT'],
      queryParams: { elegirLiga: 1 },
    },
    {
      nombre: 'Panel Root',
      ruta: RoutesUrl.DASHBOARD_ROOT,
      icono: 'tt_am-dashboard',
      tipo: ['GLOBAL'],
      roles: ['ROOT', 'CRM ROOT'],
    },

    {
      nombre: 'Mi Equipo',
      ruta: RoutesUrl.MIEQUIPO,
      icono: 'tt_cronometro',
      tipo: ['EQUIPO'],
      roles: ['PROPIETARIO', 'ROOT_EQUIPO', 'ADMINISTRADOR'],
    },
    {
      nombre: 'Reportes',
      icono: 'tt_silbato',
      desplegado: true,
      tipo: ['GLOBAL', 'LIGA'],
      roles: ['ADMIN'],
      //permiso: 'ver_finanzas'
      hijos: [
        {
          nombre: 'Marcadores',
          icono: '',
          nietos: [
            {
              nombre: 'Por ID',
              ruta: RoutesUrl.REPORTES_MARC_ID,
              icono: '',
              preserveQueryParams: true
            },
            {
              nombre: 'Por Sede',
              ruta: RoutesUrl.REPORTES_MARC_SEDE,
              icono: '',
              preserveQueryParams: true
            },
            {
              nombre: 'Por Categoría',
              ruta: RoutesUrl.REPORTES_MARC_CATEGORIA,
              icono: '',
              preserveQueryParams: true
            },
            /*{
              nombre: 'Por Fecha',
              ruta: RoutesUrl.REPORTES_MARC_FECHA,
              icono: ''
            },*/
            {
              nombre: 'Por Tipo',
              ruta: RoutesUrl.REPORTES_MARC_TIPO,
              icono: '',
              preserveQueryParams: true
            },
            {
              nombre: 'Por Equipo',
              ruta: RoutesUrl.REPORTES_MARC_EQUIPO,
              icono: '',
              preserveQueryParams: true
            },
          ]
        },
        {
          nombre: 'Incidencias',
          icono: '',
          nietos: [
            {
              nombre: 'Incidencias',
              ruta: RoutesUrl.REPORTES_MARC_INCIDENCIAS,
              icono: '',
              preserveQueryParams: true
            },
            {
              nombre: 'Suspensiones',
              ruta: RoutesUrl.REPORTES_MARC_SUSPENSIONES,
              icono: '',
              preserveQueryParams: true
            },
          ]
        },
        /*{
          nombre: 'Partidos x Equipo',
          ruta: RoutesUrl.REPORTES_EQ_PARTIDOS,
          icono: ''
        }*/
      ]
    },

    {
      nombre: 'Equipos',
      ruta: RoutesUrl.EQUIPOS,
      icono: 'tt_cronometro',
      tipo: ['GLOBAL', 'LIGA'],
      roles: ['ADMIN'],
    },
    {
      nombre: 'Ligas',
      ruta: RoutesUrl.LIGAS,
      icono: 'tt_configuracion',
      tipo: ['GLOBAL'],
      roles: ['ROOT', 'ADMIN'],
    },
    {
      nombre: 'Sedes',
      ruta: RoutesUrl.SEDES,
      icono: 'tt_estadio',
      tipo: ['GLOBAL', 'LIGA'],
      permiso: 'gestionar-sedes-liga',
    },
    {
      nombre: 'Jugadores',
      ruta: RoutesUrl.JUGADORES,
      icono: 'tt_equipos',
      tipo: ['GLOBAL', 'LIGA'],
      roles: ['ROOT', 'ADMIN'],
      //permiso: 'ver_finanzas'
    },
    {
      nombre: 'Noticias',
      ruta: RoutesUrl.NOTICIAS,
      icono: 'tt-americano-news',
      tipo: ['LIGA'],    
      roles: ['ROOT', 'ADMIN'],
    },
    {
      nombre: 'Familias',
      ruta: RoutesUrl.FAMILIAS,
      icono: 'tt_equipos',
      tipo: ['GLOBAL', 'LIGA'],
      roles: ['ROOT'],
    },
    {
      nombre: 'Jugadores',
      ruta: RoutesUrl.MISJUGADORES,
      icono: 'tt_equipos',
      tipo: ['EQUIPO'],
      permiso: 'ver-jugadores',
    },
    {
      nombre: 'Staff',
      ruta: RoutesUrl.MISTAFF,
      icono: 'tt-employee',
      tipo: ['EQUIPO'],
      permiso: 'gestionar-staff-equipo',
    },

    {
      nombre: 'Torneos',
      ruta: RoutesUrl.TORNEOS,
      icono: 'tt_trofeo',
      roles: ['ROOT', 'ADMIN','PROPIETARIO','ADMINISTRADOR', 'ROOT_EQUIPO', 'COACH'],
    },

    {
      nombre: 'Directorio de equipos',
      ruta: RoutesUrl.EQUIPOS,
      icono: 'tt-americano-chat',
      tipo: ['EQUIPO'],
      roles: ['PROPIETARIO', 'ADMINISTRADOR', 'COACH', 'FINANCIERO', 'ROOT_EQUIPO'],
      requiereLigaEquipo: true,
    },

    {
      nombre: 'CRM',
      ruta: RoutesUrl.CRM,
      icono: 'tt_crm',
      tipo: ['EQUIPO', 'GLOBAL'],
      permiso: 'crm-full',
      requiereConfigCrm: true,
    },

    {
      nombre: 'CRM Liga',
      ruta: RoutesUrl.CRMLI,
      icono: 'tt_crm',
      tipo: ['LIGA'],
      roles: ['ROOT', 'ADMIN'],
      requiereConfigCrm: true,
    },

    {
      nombre: 'Finanzas',
      ruta: '',
      icono: 'tt_invoice',
      tipo: ['EQUIPO'],
      permiso: 'gestionar-financiero-equipo',
      hijos: [
        {
          nombre: 'Flujo de efectivo',
          ruta: RoutesUrl.FIN_FLUJO,
          icono: ''
        },
        {
          nombre: 'Resumen por torneo',
          ruta: RoutesUrl.FIN_RESUMEN_TORNEOS,
          icono: ''
        },
        {
          nombre: 'Ingresos y CPC',
          ruta: RoutesUrl.FIN_INGRESOS,
          icono: ''
        },
        {
          nombre: 'Gastos',
          ruta: RoutesUrl.FIN_EGRESOS,
          icono: ''
        },
        {
          nombre: 'Comisiones',
          ruta: RoutesUrl.FIN_COMISIONES,
          icono: ''
        },
        {
          nombre: 'Configuración',
          icono: '',
          nietos:[
            {
              nombre: 'Descuentos',
              ruta: RoutesUrl.FIN_DESCUENTOS,
              icono: ''
            },
            {
              nombre: 'Categorías y plantillas',
              ruta: RoutesUrl.FIN_CONCEPTOS,
              icono: ''
            },
            {
              nombre: 'Cuentas',
              ruta: RoutesUrl.FIN_CUENTAS,
              icono: ''
            },
          ]
        },

      ]
    },

    {
      nombre: 'Finanzas Liga',
      ruta: '',
      icono: 'tt_invoice',
      tipo: ['GLOBAL', 'LIGA'],
      roles: ['ROOT', 'ADMIN'],
      hijos: [
        {
          nombre: 'Flujo de efectivo',
          ruta: RoutesUrl.FINLI_FLUJO,
          icono: ''
        },
        {
          nombre: 'Resumen por torneo',
          ruta: RoutesUrl.FINLI_RESUMEN_TORNEOS,
          icono: ''
        },
        {
          nombre: 'Ingresos y CPC',
          ruta: RoutesUrl.FINLI_INGRESOS,
          icono: ''
        },
        {
          nombre: 'Gastos',
          ruta: RoutesUrl.FINLI_EGRESOS,
          icono: ''
        },
        {
          nombre: 'Saldo de Equipos',
          ruta: RoutesUrl.FINLI_SALDOS_EQUIPOS,
          icono: ''
        },
        {
          nombre: 'Configuración',
          icono: '',
          nietos: [
            {
              nombre: 'Descuentos',
              ruta: RoutesUrl.FINLI_DESCUENTOS,
              icono: ''
            },
            {
              nombre: 'Categorías y plantillas',
              ruta: RoutesUrl.FINLI_CONCEPTOS,
              icono: ''
            },
            {
              nombre: 'Cuentas',
              ruta: RoutesUrl.FINLI_CUENTAS,
              icono: ''
            },
            {
              nombre: 'Tipos de Multa',
              ruta: RoutesUrl.FINLI_MULTAS,
              icono: ''
            },
          ]
        },
      ]
    },

    {
      nombre: 'Admin & Config',
      icono: 'tt_configuracion',
      roles: ['ROOT', 'ADMIN', 'PROPIETARIO', 'ROOT_EQUIPO', 'ADMINISTRADOR'],
      hijos: [
        {
          nombre: 'Usuarios',
          ruta: RoutesUrl.USUARIOS_ADMIN,
          icono: 'tt_equipos',
          permiso: ['gestionar-usuarios'],
        },
        {
          nombre: 'Roles',
          ruta: RoutesUrl.ROLES_EQUIPO,
          icono: 'tt_equipos',
          tipo: ['EQUIPO'],
          permiso: ['crear-roles-equipo'],
        },
        {
          nombre: 'Documentos',
          ruta: RoutesUrl.DOCS,
          icono: 'tt_am-ficha-jug'
        },
        {
          nombre: 'Tags de Noticias',
          ruta: RoutesUrl.NOTICIA_TAGS_GLOBALES,
          icono: 'tt-americano-news',
          tipo: ['GLOBAL'],
          roles: ['ROOT'],
        },
        {
          nombre: 'Categorías y Divs',
          ruta: RoutesUrl.CATEGORIAS_DIVISIONES,
          icono: 'tt_trofeo',
          permiso: ['gestionar-categorias', 'gestionar-divisiones'],
        },
        {
          nombre: 'Árbitros',
          ruta: RoutesUrl.ARBITROS,
          icono: 'tt_silbato',
          tipo: ['GLOBAL', 'LIGA'],
          permiso: ['gestionar-arbitros'],
        },
        {
          nombre: 'Enlaces cortos',
          ruta: RoutesUrl.ENLACES_CORTOS,
          icono: 'blue-link',
          tipo: ['GLOBAL'],
          permiso: ['gestionar-enlaces-cortos'],
        }
      ]
      //permiso: 'ver_finanzas'
    },



    //{ nombre: 'Torneos', ruta: RoutesUrl.TORNEOS, iconoUrl: './assets/svg/amer-torneo' },
    //{ nombre: 'Equipos', ruta: RoutesUrl.EQUIPOS, iconoUrl: './assets/svg/equipos' },
    //{ nombre: 'Sedes', ruta: RoutesUrl.SEDES, iconoUrl: './assets/svg/estadio' },
    //{ nombre: 'Configuración', ruta: RoutesUrl.CONFIGURACION, iconoUrl: 'assets/svg/configuracion' },
  ];

  public logoAMostrar = computed(() => {
    const rol = this.rolActivoSignal();
    // Prioridad: Escudo del equipo del rol activo -> Icono de la liga
    return rol?.escudoURL || this.liga?.appIcon || 'assets/images/TT-Logo-Americano.svg';
  });
  toggleColapso() {
    this.menuColapsado.update(v => !v);
  }

  toggleModoOscuro() {
    this._theming.alternarModoOscuro();
    this.modoOscuro.set(this._theming.esModoOscuroActivo());
  }

  public opcionesMenuFiltradas = computed(() => {
    // Leemos las signals para que Angular sepa que dependemos de ellas
    this.rolActivoSignal();
    this.usuarioSignal();

    return this.filtrarOpciones(this.menuCompleto);
  });

  // Vistas de agenda que necesitan todo el ancho: al entrar a ellas la barra se comprime
  private readonly _rutasConBarraComprimida = [
    RoutesUrl.TORNEOS_AJUSTAR_HORARIOS, RoutesUrl.TORNEOS_MOVER_PARTIDOS, RoutesUrl.TORNEOS_RESUMEN_JORNADAS,
  ].map(ruta => `/${RoutesUrl.MAIN}/${RoutesUrl.TORNEOS}/${ruta}`);
  // Vistas con parámetros en la ruta que también entran con la barra comprimida
  private readonly _patronesConBarraComprimida = [
    new RegExp(`^/${RoutesUrl.MAIN}/${RoutesUrl.TORNEOS}/convocatoria/\\d+/mis-inscripciones$`),
    // Configuración del torneo: más espacio para sus tarjetas y el calendario
    new RegExp(`^/${RoutesUrl.MAIN}/${RoutesUrl.TORNEOS}/\\d+/configuracion/`),
    // Financiero de equipo y de liga: tablas anchas
    new RegExp(`^/${RoutesUrl.MAIN}/(fineq|finli)/`),
  ];
  private _rutaAnterior: string | null = null;

  constructor() {
    this._comprimirSiEsAgenda(this._router.url);

    // Lógica para cerrar el menú automáticamente al navegar
    this._router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event) => {
      // Si navegamos, cerramos el menú (útil en móvil)
      this._servicioBarra.cerrarMenuLateral();
      this._comprimirSiEsAgenda((event as NavigationEnd).urlAfterRedirects);
    });
  }

  // Solo al entrar (no al cambiar filtros de la misma vista), para respetar si el usuario la vuelve a abrir
  private _comprimirSiEsAgenda(url: string): void {
    const ruta = url.split('?')[0];
    const comprime = this._rutasConBarraComprimida.includes(ruta) || this._patronesConBarraComprimida.some(p => p.test(ruta));
    if (ruta !== this._rutaAnterior && comprime) {
      this.menuColapsado.set(true);
    }
    this._rutaAnterior = ruta;
  }

  /**
   * Filtra recursivamente las opciones basándose en Roles y Permisos
   */
  private filtrarOpciones(opciones: OpcionMenu[]): OpcionMenu[] {
    const rolActivo = this._sesion.getRolActivo();

    return opciones.reduce<OpcionMenu[]>((resultado, opcion) => {

      // 1. Filtrar por tipo: si el ítem declara tipo, el contexto activo debe
      // estar en esa lista (si se omite, es visible para todos).
      if (opcion.tipo && (!rolActivo || !opcion.tipo.includes(rolActivo.tipo))) {
        return resultado;
      }

      // 2. Validar ROL
      if (opcion.roles && !this._sesion.verificarPermisoRol(opcion.roles)) {
        return resultado;
      }

      if (opcion.rolesExcluidos && this._sesion.verificarPermisoRol(opcion.rolesExcluidos)) {
        return resultado;
      }

      // 2. Validar PERMISO (si es array, basta con tener cualquiera de ellos)
      if (opcion.permiso) {
        const permisos = Array.isArray(opcion.permiso) ? opcion.permiso : [opcion.permiso];
        if (!permisos.some(p => this._sesion.can(p))) return resultado;
      }

      // 2b. Exige que exista una CrmConfig para el contexto activo
      if (opcion.requiereConfigCrm && !this._crmConfigDisponible()) return resultado;

      // 2c. Exige que el equipo activo pertenezca a alguna liga
      if (opcion.requiereLigaEquipo && !this._equipoTieneLiga()) return resultado;

      let opcionFiltrada = opcion;

      // 3. Filtrar HIJOS de forma recursiva y reemplazarlos por la lista ya filtrada
      if (opcion.hijos && opcion.hijos.length > 0) {
        const hijosFiltrados = this.filtrarOpciones(opcion.hijos);
        if (hijosFiltrados.length === 0) return resultado;
        opcionFiltrada = { ...opcionFiltrada, hijos: hijosFiltrados };
      }

      // 4. Filtrar NIETOS de forma recursiva y reemplazarlos por la lista ya filtrada
      if (opcion.nietos && opcion.nietos.length > 0) {
        const nietosFiltrados = this.filtrarOpciones(opcion.nietos);
        if (nietosFiltrados.length === 0) return resultado;
        opcionFiltrada = { ...opcionFiltrada, nietos: nietosFiltrados };
      }

      resultado.push(opcionFiltrada);
      return resultado;
    }, []);
  }

  // Nombre del feed de noticias personalizable por liga (liga.config.nombreFeedNoticias)
  public obtenerNombreOpcion(opcion: OpcionMenu): string {
    if (opcion.ruta === RoutesUrl.NOTICIAS) {
      return this.liga?.config?.nombreFeedNoticias || opcion.nombre;
    }
    return opcion.nombre;
  }

  cerrarMenu() {
    this._servicioBarra.cerrarMenuLateral();
  }

  // Con la barra comprimida, el ícono padre se marca si la ruta actual es de alguno de sus submenús
  tieneHijoActivo(opcion: OpcionMenu): boolean {
    const rutas = (opcion.hijos ?? []).flatMap(h => [h.ruta, ...(h.nietos ?? []).map(n => n.ruta)]).filter(Boolean) as string[];
    return rutas.some(ruta => this._router.isActive(`/${RoutesUrl.MAIN}/${ruta}`, { paths: 'subset', queryParams: 'ignored', fragment: 'ignored', matrixParams: 'ignored' }));
  }

  toggleMenu(opcion: OpcionMenu) {
    if (opcion.hijos || opcion.nietos) {
      opcion.desplegado = !opcion.desplegado;
    }
  }
}
